import {FormControl, FormGroup, Validators} from "@angular/forms";

export interface ItsmAddressModel {
  id?: string;
  name1: string;
  name2: string;
  line1: string;
  line2: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  businessPartnerId?: string;
  itsmName?: string; // Used for selection
  itsmSiteId?: string;
  itsmSiteName?: string;
  itsmBuildingId?: string;
  itsmBuilding?: string;
  customShippingLabel1?: string;
  customShippingLabel2?: string;
  deliveryType?: string;
  sameAddressKey?: string;
  buildingTypes?: string[];
  paymentTerms?: string;
}

export interface ItsmSiteSettableAddressesModel {
  errors?: string[];
  physicalAddressId?: string;
  tonerAddressId?: string;
  serviceAddressId?: string;
  hpInstalledKit?: boolean;
  settableAddresses?: ItsmAddressModel[];
  itsmSiteAddressId?: string;
  itsmSiteName?: string;
  tmcSiteName?: string;
  defaultCustomShippingLabel1?: string;
  defaultCustomShippingLabel2?: string;
  deliveryType?: string;
  buildingTypes?: string[];
}

export interface ItsmProjectSettableAddressesModel {
  errors?: string[];
  settableAddresses?: ItsmAddressModel[];
  physicalAddressId?: string;
  tonerAddressId?: string;
  serviceAddressId?: string;
}

export function itsmAddressModelFactory(json?: any): ItsmAddressModel {
  json = json || {};

  // create a key for this address that can identify identical addresses
  const name1 = (json.name1 || "").trim();
  const name2 = (json.name2 || "").trim();
  const name = name1 ? `${name1} ${name2}`.trim() : name2;

  const line1 = (json.line1 || "").trim();
  const line2 = (json.line2 || "").trim();
  const line = line1 ? `${line1} ${line2}`.trim() : line2;

  let addr = {
    id: json.id || "",
    name1: json.name1 || "",
    name2: json.name2 || "",
    line1: json.line1 || "",
    line2: json.line2 || "",
    city: json.city || "",
    region: json.region || "",
    postalCode: json.postalCode || "",
    country: json.country || "",
    businessPartnerId: json.businessPartnerId || "",
    itsmName: json.itsmName || json.itsmBuilding || name || line || "Unknown", // Must be set for selection
    itsmSiteId: json.itsmSiteId || "",
    itsmSiteName: json.itsmSiteName || "",
    itsmBuildingId: json.itsmBuildingId || "",
    itsmBuilding: json.itsmBuilding || "",
    customShippingLabel1: json.customShippingLabel1 || "",
    customShippingLabel2: json.customShippingLabel2 || "",
    deliveryType: json.deliveryType || "",
    sameAddressKey: null,
    buildingTypes: json.buildingTypes || null,
    paymentTerms: json.paymentTerms || "",
  };
  addr.sameAddressKey = JSON.stringify([
    (addr.itsmBuilding || "").trim().toLowerCase(), // Must be here since ITSM key's on building name
    name.toLowerCase(),
    line.toLowerCase(),
    (addr.city || "").trim().toLowerCase(),
    addr.region || null,
    (addr.postalCode || "").trim().toLowerCase(),
    addr.country || null,
    addr.itsmSiteId || null,
    addr.customShippingLabel1 || null,
    addr.customShippingLabel2 || null,
    addr.deliveryType || null,
    addr.buildingTypes || null,
  ]);

  return addr;
}

function trimHelper(field: string): string {
  if (!field) return "";
  return field.trim();
}

export function itsmCreatableAddressModelFactor(
  json: any,
  itsmSiteId: string,
  itsmSiteName: string,
  allowBpId: boolean = false,
): ItsmAddressModel {
  json = json || {};

  return {
    businessPartnerId: (allowBpId ? trimHelper(json.name1) : ""),
    name1: trimHelper(json.name1),
    name2: trimHelper(json.name2),
    line1: trimHelper(json.line1),
    line2: trimHelper(json.line2),
    city: trimHelper(json.city),
    region: json.region || null,
    postalCode: trimHelper(json.postalCode),
    country: trimHelper(json.country),
    itsmSiteId,
    itsmSiteName,
    itsmBuilding: trimHelper(json.itsmBuilding),
    customShippingLabel1: trimHelper(json.customShippingLabel1),
    customShippingLabel2: trimHelper(json.customShippingLabel2),
    deliveryType: json.deliveryType || null,
    buildingTypes: json.buildingTypes || null,
  };
}

export function itsmAddressModelListFactory(json?: any): ItsmAddressModel[] {
  return Array.from(json || []).map(it => itsmAddressModelFactory(it)) as ItsmAddressModel[];
}

export function itsmSettableAddressesModelFactory(json?: any): ItsmSiteSettableAddressesModel {
  json = json || {};

  return {
    errors: json.errors || [],
    physicalAddressId: json.physicalAddressId || "",
    tonerAddressId: json.tonerAddressId || "",
    serviceAddressId: json.serviceAddressId || "",
    hpInstalledKit: !!json.hpInstalledKit,
    settableAddresses: itsmAddressModelListFactory(json.settableAddresses) || [],
    itsmSiteAddressId: json.itsmSiteAddressId || "",
    itsmSiteName: json.itsmSiteName || "",
    tmcSiteName: json.tmcSiteName || "",
    defaultCustomShippingLabel1: json.defaultCustomShippingLabel1 || "",
    defaultCustomShippingLabel2: json.defaultCustomShippingLabel2 || "",
  };
}

export function itsmProjectSettableAddressesModelFactory(json?: any): ItsmProjectSettableAddressesModel {
  json = json || {};

  return {
    errors: json.errors || [],
    tonerAddressId: json.tonerAddressId || "",
    serviceAddressId: json.serviceAddressId || "",
    physicalAddressId: json.physicalAddressId || "",
    settableAddresses: itsmAddressModelListFactory(json.settableAddresses) || [],
  };
}

export interface ItsmAddressesForProjectModel {
  errors: string[];
  billTo: ItsmAddressModel[];
  soldTo: ItsmAddressModel[];
  payer: ItsmAddressModel[];
  endCustomer: ItsmAddressModel[];
  endUser: ItsmAddressModel[];
}

export function itsmAddressesForProjectModelFactory(json?: any): ItsmAddressesForProjectModel {
  json = json || {};

  return {
    errors: json.errors,
    billTo: itsmAddressModelListFactory(json.billTo) || [],
    soldTo: itsmAddressModelListFactory(json.soldTo) || [],
    payer: itsmAddressModelListFactory(json.payer) || [],
    endCustomer: itsmAddressModelListFactory(json.endCustomer) || [],
    endUser: itsmAddressModelListFactory(json.endUser) || [],
  };
}

export function addressModelFormGroup(address?: ItsmAddressModel): FormGroup {
  address = address || itsmAddressModelFactory();

  return new FormGroup({
    name1: new FormControl(address.name1, [Validators.required, Validators.maxLength(100)]),
    name2: new FormControl(address.name2, [Validators.maxLength(100)]),
    line1: new FormControl(address.line1, [Validators.required, Validators.maxLength(100)]),
    line2: new FormControl(address.line2, [Validators.maxLength(100)]),
    city: new FormControl(address.city, [Validators.required, Validators.maxLength(40)]),
    region: new FormControl(address.region, [Validators.required, Validators.maxLength(40)]),
    postalCode: new FormControl(address.postalCode, [Validators.required, Validators.maxLength(10)]),
    country: new FormControl(address.country, [Validators.required, Validators.maxLength(2)]),
    businessPartnerId: new FormControl(address.businessPartnerId, [Validators.maxLength(11)]),
    itsmBuilding: new FormControl(address.itsmBuilding, [Validators.required, Validators.maxLength(100)]),
    customShippingLabel1: new FormControl(address.customShippingLabel1, [Validators.maxLength(200)]),
    customShippingLabel2: new FormControl(address.customShippingLabel2, [Validators.maxLength(200)]),
    deliveryType: new FormControl(address.deliveryType, [Validators.maxLength(30)]), // Not stored locally
    buildingTypes: new FormControl(address.buildingTypes, [Validators.required]),
    // all the other parameters are cant be set in the UI.
  });
}
